export type PaginationItem = number | 'ellipsis'

export interface SanitizedPaginationResult {
  current: number;
  total: number;
}

/**
 * Sanitizes input numbers to ensure safe boundary calculations.
 */
export const sanitizePagination = (
  currentPage: number,
  totalPages: number
): SanitizedPaginationResult => {
  const parsedTotalPages = Number.isNaN(Number(totalPages)) ? 0 : Number(totalPages)
  const parsedCurrentPage = Number.isNaN(Number(currentPage)) ? 1 : Number(currentPage)

  const total = Math.max(0, Math.floor(parsedTotalPages))
  const effectiveMaxTotal = Math.max(1, total)
  const current = Math.max(1, Math.min(parsedCurrentPage, effectiveMaxTotal))

  return { current, total }
}

/**
 * Generates an array of page numbers and 'ellipsis' tokens to build a sliding window pagination bar
 * @param current - The active page index
 * @param total - The absolute total number of pages
 * @param maxVisibleButtons - The maximum allowed slots allocated for numeric and ellipsis elements in the UI
 * @returns An array containing page numbers and 'ellipsis' string placeholders.
 */
export function generatePaginationItems (
  current: number,
  total: number,
  maxVisibleButtons: number
): PaginationItem[] {
  // CHECKPOINT 1: Immediate short-circuit
  if (total <= maxVisibleButtons) {
    return Array.from({ length: total }, (_, index) => index + 1)
  }

  // CHECKPOINT 2: Define deterministic thresholds
  // to determine the current page is close to the which edge boundary
  const leftThreshold = Math.ceil(maxVisibleButtons / 2)
  const rightThreshold = total - Math.floor(maxVisibleButtons / 2)

  // CHECKPOINT 3: Assemble exactly based on the 3 possible layout states
  if (current <= leftThreshold) {
    // STATE 1: Close to start
    // E.g., [1, 2, 3, 4, 5, 6, 'ellipsis', 10]
    const reservedSlots = 2 // Reserve 2 slots: one ellipse and last
    const leftItemsCount = maxVisibleButtons - reservedSlots // capturing dynamic item count only
    const start = 1
    const leftItems = Array.from({ length: leftItemsCount }, (_, i) => start + i)

    return [...leftItems, 'ellipsis', total]
  } else if (current >= rightThreshold) {
    // STATE 2: Close to end
    // E.g., [1, 'ellipsis', 5, 6, 7, 8, 9, 10]
    const reservedSlots = 2 // Reserve 2 slots: first and one ellipse
    const rightItemsCount = maxVisibleButtons - reservedSlots // capturing dynamic item count only
    const start = total - rightItemsCount
    const rightItems = Array.from({ length: rightItemsCount }, (_, i) => start + i)

    return [1, 'ellipsis', ...rightItems]
  } else {
    // STATE 3: Middle (Both ellipses needed)
    // E.g., [1, 'ellipsis', 3, 4, 5, 6, 'ellipsis', 10]
    const reservedSlots = 4 // Reserve 4 slots: first, last, and 2 ellipses
    const centerItemsCount = maxVisibleButtons - reservedSlots // capturing dynamic item count only
    const start = current - Math.floor(centerItemsCount / 2)
    const centerItems = Array.from({ length: centerItemsCount }, (_, i) => start + i)

    return [1, 'ellipsis', ...centerItems, 'ellipsis', total]
  }
}
